import { QueryExpr } from '@apis/Resources';
import { Group, NumberInput as BaseNumberInput, Popover, Select, Sx, Text, Textarea, TextInput, useMantineTheme } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { FillerSwitch } from '@root/Design/Filler';
import { useNumberInputFractions } from '@root/Design/Data';
import { SettingsInputBox, SettingsInputRow, SettingsInputStack, SettingsLabel } from '@root/Design/Settings';
import { useEllipsisTextClass } from '@root/Design/Text';
import { EventEmitter, useEvent, useToggle } from '@root/Services/EventEmitter';
import { useFmtSvc } from '@root/Services/FormatService';
import { asExpr, PrimitiveType } from '@root/Services/QueryExpr';
import { DependencyList, useCallback, useEffect, useMemo, useState } from 'react';
import { Keyboard, List, Percentage } from 'tabler-icons-react';
import { DropdownOpener } from '../Picker/Design';
import { Picker } from '../Picker/Picker';
import { ExprPicker, ExprPickerAdornment } from './ExprSettings';
import {
    getAccessorKey,
    getInputBinding,
    IBaseExprInputSpec,
    IBaseInputSpec,
    IDateInputSpec,
    IInputSpecBuilderDone,
    IInputSpecBuilderType,
    IMultiItemPickerSpec,
    IMultilineStringInputSpec,
    InputSpec,
    InputSpecBuilder,
    INumberInputSpec,
    ISingleItemPickerSpec,
    IStringInputSpec,
    ModelAccessor,
    wrapOnChange,
} from './InputSpec';

type ISettingsInputProps<TType extends InputSpec['type'] = any> =
    | { spec?: InputSpec; onChanged?: (element: InputSpec) => void }
    | ({ type: TType } & InputSpec);
export function SettingsInput<TType extends InputSpec['type'] = any>(props: ISettingsInputProps<TType>) {
    const { spec, onChanged } = usePropsAsSpec(props);
    const wrappedSpec = useRerenderOnChange(spec, onChanged);

    return (
        <SettingsLabeled spec={wrappedSpec}>
            <InputSpecInput spec={wrappedSpec} />
        </SettingsLabeled>
    );
}

function usePropsAsSpec(props: ISettingsInputProps) {
    const spec = 'spec' in props ? props.spec : (props as InputSpec);
    const deps: DependencyList = 'spec' in props ? [spec, props.onChanged] : [spec?.onChange, spec?.icon, JSON.stringify(spec)];
    return useMemo(() => {
        return {
            spec: 'spec' in props && !!props.spec ? props.spec : ({ ...props } as InputSpec),
            onChanged: 'spec' in props ? props.onChanged : undefined,
        };
    }, deps);
}

export function useSpecBuilder<TData, TResult>(
    data: TData,
    build: (builder: IInputSpecBuilderType<TData>) => TResult & IInputSpecBuilderDone<TData, any, any>
): TResult extends IInputSpecBuilderDone<TData, infer U, any> ? U : never {
    return useMemo(() => {
        const builder = InputSpecBuilder.create<TData>();
        const built = build(builder);
        return built.bind(data);
    }, [data]);
}

export function useInputBinding<TModel, TModelValue>(model: TModel, accessor: ModelAccessor<TModel, TModelValue>, defaultValue?: TModelValue) {
    const rerender = useRerender();
    return useMemo(() => {
        const result = getInputBinding<TModel, TModelValue>(model, accessor, defaultValue);
        return wrapOnChange(result, { afterChange: rerender });
    }, [model, getAccessorKey(accessor, defaultValue)]);
}

export function useRerenderOnChange<T extends InputSpec['type']>(spec: InputSpec & { type: T }, onChanged?: (element: InputSpec) => void) {
    const rerender = useRerender();
    return useMemo(() => {
        return wrapOnChange(spec as any, {
            afterChange: () => {
                rerender();
                onChanged?.(spec);
            },
        }) as typeof spec;
    }, [spec]);
}

function useRerender() {
    const [, update] = useState(0);
    return useCallback(() => update((prev) => prev + 1), []);
}

export function InputSpecInput<T extends InputSpec>(props: { spec: T }) {
    const { type } = props.spec;
    switch (type) {
        case 'string':
            return <SettingsStringInput spec={props.spec} />;
        case 'string-multiline':
            return <SettingsMultilineStringInput spec={props.spec} />;
        case 'number':
            return <SettingsNumberInput spec={props.spec} />;
        case 'date':
            return <SettingsDateInput spec={props.spec} />;
        case 'item-picker':
            return <ItemPickerInput spec={props.spec as ISingleItemPickerSpec<any>} />;
        default:
            if (type.endsWith('-expr')) {
                return <SettingsExprInput spec={props.spec} />;
            }
            return null;
    }
}

export function SettingsLabeled(props: { spec: IBaseInputSpec; children: React.ReactNode }) {
    const { label, tooltip, icon, presentationOptions: pres = {} } = props.spec;
    const fullW = 'fullWidth' in pres ? (pres as { fullWidth?: boolean }).fullWidth : false;
    const Container = fullW ? SettingsInputStack : SettingsInputRow;
    return (
        <Container>
            <SettingsLabel tooltip={tooltip} icon={icon}>
                {label}
            </SettingsLabel>
            {props.children}
        </Container>
    );
}

// #region Value Inputs
function SettingsStringInput(props: { spec: IStringInputSpec }) {
    const { value, onChange, options, presentationOptions } = props.spec;
    const { align, decorLeft, decorRight, fullWidth, placeholder, width } = presentationOptions ?? {};
    const handleChange = useCallback(
        (e: string | null | React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(typeof e === 'string' ? e : !e ? '' : e.currentTarget.value),
        [onChange]
    );

    const inputSx: Sx = {
        width: fullWidth ? undefined : `var(--input-width, ${width ?? 140}px)`,
        input: { textAlign: align === 'center' ? 'center' : align === 'right' ? 'right' : 'left' },
        ['.mantine-Select-dropdown *']: { textAlign: align === 'center' ? 'center' : align === 'right' ? 'right' : 'left' },
    };

    const inputProps = { size: 'xs' as const, placeholder, sx: inputSx, value, onChange: handleChange, rightSection: decorRight, icon: decorLeft };

    return options?.length ? <Select data={options} {...inputProps} /> : <TextInput {...inputProps} />;
}

function SettingsMultilineStringInput(props: { spec: IMultilineStringInputSpec }) {
    const { value, onChange, presentationOptions } = props.spec;
    const { placeholder } = presentationOptions ?? {};
    const handleChange = useCallback(
        (e: string | null | React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(typeof e === 'string' ? e : !e ? '' : e.currentTarget.value),
        [onChange]
    );

    return <Textarea size="xs" minRows={2} maxRows={5} autosize placeholder={placeholder} value={value} onChange={handleChange} />;
}

function SettingsNumberInput(props: { spec: INumberInputSpec }) {
    const { defaultValue, value, onChange, allowEmpty, mask, min, max, fractions: explicitFractions, presentationOptions } = props.spec;
    let { align, decorLeft, decorRight, width } = presentationOptions ?? {};
    const [toValue, toDisplay, parser, formatter] = useMemo(() => {
        let toValue = (v: number) => v;
        let toDisplay = (v: number) => v;
        let parser: undefined | ((v?: string) => string) = undefined;
        let formatter: undefined | ((v?: string) => string) = undefined;
        if (mask === 'percent') {
            toValue = (v: number) => v / 100;
            toDisplay = (v: number) => v * 100;
            parser = (v?: string) => v?.replace(/[%]*/g, '') ?? '';
            formatter = (v?: string) => `${v}%`;
        }
        return [toValue, toDisplay, parser, formatter];
    }, [mask]);
    const fractionProps = useNumberInputFractions(value ?? defaultValue, explicitFractions, { parser, formatter });

    const handleChange = useCallback(
        (e: number | undefined) => {
            const num = parseFloat((e ?? '').toString());
            if (isNaN(num)) return;
            onChange(toValue(num));
        },
        [onChange, allowEmpty, toValue]
    );

    const digits =
        Math.max(min?.toString().length ?? 0, max?.toString().length ?? 0, defaultValue?.toString().length ?? 0) + (explicitFractions ?? 0);
    align = align ?? (mask === 'percent' ? 'center' : 'right');
    width = width ?? (mask === 'percent' ? 80 : width ?? 60 + digits * 8);

    const numberSx: Sx = {
        width: `var(--input-width, ${width}px)`,
        ['input']: align === 'center' ? { textAlign: 'center', paddingLeft: 0 } : align === 'right' ? { textAlign: 'right', paddingRight: 25 } : {},
        ['input:disabled']: { cursor: 'default' },
    };

    return (
        <BaseNumberInput
            size="xs"
            sx={numberSx}
            min={min}
            max={max}
            stepHoldDelay={300}
            stepHoldInterval={100}
            {...fractionProps}
            value={value === null ? undefined : toDisplay(value)}
            onChange={handleChange}
            rightSectionProps={{}}
            rightSection={decorRight}
            icon={decorLeft}
        />
    );
}

function SettingsDateInput(props: { spec: IDateInputSpec }) {
    const fmtSvc = useFmtSvc();
    const { value: rawValue, onChange, presentationOptions } = props.spec;
    const { optional } = presentationOptions ?? {};
    const value = typeof rawValue === 'string' ? fmtSvc.toLocalDate(rawValue) : rawValue;
    const sx: Sx = {
        width: 'var(--input-width, 140px)',
        input: { textAlign: 'center' },
    };
    return (
        <DatePicker fixOnBlur sx={sx} inputFormat="MMM D, YYYY" clearable={!!optional} size="xs" value={value} onChange={onChange} allowFreeInput />
    );
}
// #endregion

// #region Expression Input
function SettingsExprInput(props: { spec: InputSpec }) {
    const { spec } = props;
    const { value, type, onChange: rawOnChange, defaultValue, ...remainingSpec } = spec as IBaseExprInputSpec & InputSpec;
    const { acceptConstant, acceptExpr, defaultMode, schemaCtx, operations, ...constantSpecSettings } = remainingSpec;
    const onChange = rawOnChange as (expr: QueryExpr) => void;

    const { defaultExpr, expr, types, initialMode, valueType } = useMemo(() => {
        const typeParts = props.spec.type.split('-');
        typeParts.pop();
        const rawValueType = typeParts.join('-');
        const valueType: InputSpec['type'] | 'unknown' =
            rawValueType === 'string' ? 'string' : rawValueType === 'number' ? 'number' : rawValueType === 'date' ? 'date' : 'unknown';

        const expr = asExpr(value);
        const defaultExpr = asExpr(defaultValue) ?? (!acceptConstant || !defaultValue ? undefined : { Value: defaultValue });
        const initialMode =
            acceptConstant === false ? 'expr' : !expr ? defaultMode : 'Value' in expr ? 'constant' : acceptExpr !== false ? 'expr' : 'constant';

        return { valueType, initialMode, defaultExpr, expr, types: [valueType] };
    }, [JSON.stringify([value, defaultValue]), type, defaultMode]);

    const [mode, setMode] = useState<'expr' | 'constant'>(initialMode ?? 'expr');

    const { exprOpener, switchToConstant, constantSpec } = useMemo(() => {
        const exprValue = !expr || !('Value' in expr) ? undefined : expr.Value;
        const defaultValue = !defaultExpr || !('Value' in defaultExpr) ? undefined : defaultExpr.Value;
        const value = exprValue ?? defaultValue;
        const constantSpec = {
            ...constantSpecSettings,
            type: valueType,
            onChange: (value: string | number | Date) => onChange({ Value: value }),
            value,
            defaultValue,
        } as InputSpec;
        return {
            exprOpener: (open: () => void) => (
                <Group noWrap spacing={4}>
                    <InputSpecInput spec={constantSpec} />
                    <ExprPickerAdornment color="primary" tooltip="Switch to dynamic data point" icon={List} onClick={open} />
                </Group>
            ),
            switchToConstant: () => {
                onChange({ Value: defaultValue } as QueryExpr);
                setMode('constant');
            },
            constantSpec,
        };
    }, [onChange, valueType, constantSpecSettings, JSON.stringify([expr, defaultExpr])]);

    const opener = mode === 'expr' ? undefined : exprOpener;
    const rightSection =
        mode === 'constant' || !acceptConstant ? undefined : (
            <ExprPickerAdornment color="primary" tooltip="Switch to manually entry" icon={Keyboard} onClick={switchToConstant} />
        );

    const handleExprPick = useCallback(
        (expr: QueryExpr) => {
            onChange(expr);
            setMode('expr');
        },
        [onChange]
    );

    return acceptExpr === false ? (
        <InputSpecInput spec={constantSpec} />
    ) : (
        <ExprPicker key={mode} {...{ expr, onChange: handleExprPick, opener, operations, rightSection, schemaCtx, types }} />
    );
}
// #endregion Expression Input

// #region Item Picker Input
function ItemPickerInput({ spec }: { spec: ISingleItemPickerSpec<any> | IMultiItemPickerSpec<any> }) {
    const theme = useMantineTheme();
    const { childAccessor, items: itemSource, mode, nameAccessor, onChange, value, defaultValue, presentationOptions, required } = spec;
    const { listFilterable, listHeight, listWidth, placeholder, preload, renderItem, width } = presentationOptions ?? {};
    const [opened, { close, toggle }] = useToggle(false);

    const { items, itemsChanged, loading, load } = useMemo(() => {
        const itemsChanged = EventEmitter.empty();
        let items = Array.isArray(itemSource) ? itemSource : null;
        let loading = false;
        let loaded = false;
        const load = async () => {
            if (typeof itemSource === 'function' && !loading && !loaded) {
                loading = true;
                itemsChanged.emit();
                try {
                    items = await itemSource();
                } finally {
                    loading = false;
                    loaded = true;
                    itemsChanged.emit();
                }
            }
        };
        return {
            itemsChanged,
            get items() {
                return items;
            },
            get loading() {
                return loading;
            },
            get load() {
                return load;
            },
        };
    }, [itemSource]);
    useEvent(itemsChanged);

    const handleChange = useCallback(
        (selected: unknown[]) => {
            if (mode === 'single') {
                onChange(selected[0] ?? defaultValue);
                close();
            } else {
                onChange(selected as any);
            }
        },
        [onChange, mode]
    );

    useEffect(() => {
        if (preload) {
            load();
        }
    }, []);
    useEffect(() => {
        if (opened && !preload) {
            load();
        }
    }, [opened, load]);

    const childAcc = useCallback((item: any) => childAccessor?.(item) || undefined, [childAccessor]);

    const values = useMemo(() => (Array.isArray(value) ? value : [value]), [value]);
    const valueNames = values?.map((item) => (typeof item === 'object' && nameAccessor ? nameAccessor(item) : item));
    const valueCtLbl = mode === 'single' ? '' : `(${values.length}) `;
    const valuesLbl = !values.length ? null : valueCtLbl + valueNames?.join(', ');
    const hasValue = values.length > 0;

    const filterable = typeof listFilterable === 'boolean' ? listFilterable : (items?.length ?? 0) > (listFilterable ?? 10);

    const opener = <DropdownOpener size="sm" onClick={() => {}} state={loading ? 'loading' : opened ? 'opened' : 'closed'} />;
    const state = opened ? 'active' : !hasValue && required ? 'invalid' : ('valid' as const);
    const ellipsisText = useEllipsisTextClass();

    const loadingSx: Sx = loading ? { height: 200, width: 300, background: 'white' } : {};
    return (
        <Popover withinPortal opened={opened} shadow="md" onClose={close} position="bottom" offset={0} radius="md" zIndex={1000}>
            <Popover.Target>
                <SettingsInputBox style={{ width }} state={state} onClick={toggle} rightAdornment={opener}>
                    <Text size={13} className={ellipsisText} span color={hasValue || required ? undefined : 'dimmed'} italic={!hasValue}>
                        {hasValue ? valuesLbl : placeholder}
                    </Text>
                </SettingsInputBox>
            </Popover.Target>
            <Popover.Dropdown p={0} sx={{ ...loadingSx, border: `solid 1px ${theme.colors.gray[3]}` }}>
                <FillerSwitch loading={loading} noData={!items?.length}>
                    {() => (
                        <Picker
                            items={items!}
                            mode={mode}
                            nameAccessor={nameAccessor}
                            onChange={handleChange}
                            childAccessor={childAcc}
                            selections={values}
                            height={listHeight ?? 350}
                            width={listWidth ?? 400}
                            minimizeHeight
                            renderItem={renderItem}
                            inlineFullText
                            noFilter={!filterable}
                        />
                    )}
                </FillerSwitch>
            </Popover.Dropdown>
        </Popover>
    );
}
// #endregion Item Picker Input
